import { ThemeConfig as ANTDThemeConfig } from 'antd';
import { DefaultTheme } from 'styled-components';

import { rem } from '../../utils';

export const applyThemeForAntd = (theme: DefaultTheme): ANTDThemeConfig => {
  if (!theme) {
    throw new Error(`Theme is ${theme}`);
  }

  const antdThemeConfig: ANTDThemeConfig = {
    token: {
      fontFamily: theme.Typography.FontFamily,
      colorTextBase: theme.Color.Neutrals.Text,
      colorTextPlaceholder: theme.Color.Neutrals.Gray,
      colorBgContainer: theme.Color.Neutrals.Paper,
      colorBorderSecondary: theme.Color.Neutrals.Border,
      colorBgContainerDisabled: theme.Color.Neutrals.Light,
      colorBgElevated: theme.Color.Neutrals.Surface,
      colorBgTextHover: theme.Color.Brand.Primary,
      colorBorder: theme.Color.Neutrals.Border,
      colorPrimary: theme.Color.Brand.Primary,
      colorPrimaryBorder: theme.Color.Brand.Primary,
      colorPrimaryHover: theme.Color.Brand.Primary,
      colorText: theme.Color.Neutrals.Text,
      colorTextHeading: theme.Color.Neutrals.Text,
      colorTextDisabled: theme.Color.Neutrals.Gray,
      colorWhite: theme.Color.NeutralsFixed.Paper,
      borderRadius: theme.Border.Radius.Small,
      borderRadiusLG: theme.Border.Radius.Large,
    },
    components: {
      Select: {
        borderRadiusLG: theme.Border.Radius.Medium,
        colorBorder: theme.Color.Neutrals.Border,
        colorText: theme.Color.Neutrals.Dark,
        fontFamily: theme.Typography.FontFamily,
        optionSelectedBg: theme.Color.Neutrals.Light,
        showArrowPaddingInlineEnd: theme.Spacing.Size.MediumLarge,
      },
      Form: {
        verticalLabelMargin: `${theme.Spacing.Size.Small}px 0`,
        itemMarginBottom: theme.Spacing.Size.Small,
        labelRequiredMarkColor: theme.Color.Feedback.Alert,
      },
      Input: {
        fontFamily: theme.Typography.FontFamily,
        hoverBorderColor: theme.Color.Brand.Primary,
        hoverBg: theme.Color.Neutrals.Paper,
        activeBorderColor: theme.Color.Brand.Primary,
        activeBg: theme.Color.Neutrals.Paper,
        colorBorder: theme.Color.Neutrals.Border,
        colorText: theme.Color.Neutrals.Text,
        addonBg: theme.Color.Neutrals.Light,
        borderRadiusLG: theme.Border.Radius.Medium,
      },
      Menu: {
        fontFamily: theme.Typography.FontFamily,
        itemSelectedColor: theme.Color.Brand.Primary,
        itemSelectedBg: theme.Color.Brand.Lightest,
        itemColor: theme.Color.Neutrals.Text,
        iconMarginInlineEnd: theme.Spacing.Size.XSmall,
        colorBgTextHover: theme.Color.Brand.Lightest,
        activeBarBorderWidth: 0,
        colorBgContainer: theme.Color.Neutrals.Paper,
      },
      Tabs: {
        inkBarColor: theme.Color.Brand.Primary,
        itemActiveColor: theme.Color.Brand.Primary,
        itemColor: theme.Color.Neutrals.Text,
        itemHoverColor: theme.Color.Brand.Primary,
        itemSelectedColor: theme.Color.Brand.Primary,
        fontFamily: theme.Typography.FontFamily,
        colorBorderSecondary: theme.Color.Neutrals.Border,
      },
      Modal: {
        colorBgTextHover: 'unset',
        wireframe: true,
        contentBg: theme.Components.Panel.Background,
        headerBg: 'transparent',
      },
      Tag: {
        defaultColor: theme.Color.Brand.Primary,
        defaultBg: theme.Color.Brand.Lightest,
        colorBorder: theme.Color.Brand.Primary,
        borderRadiusSM: theme.Border.Radius.XSmall,
        marginXS: 0,
      },
      Button: {
        controlHeight: theme.Button.Size.Medium,
        paddingBlock: theme.Spacing.Size.XSmall,
        paddingInline: theme.Spacing.Size.Small,
        primaryShadow: 'none',
        colorBorder: theme.Color.Neutrals.Border,
        dangerShadow: 'none',
      },
      Divider: {
        margin: 0,
        colorIconHover: theme.Color.Brand.Primary,
      },
      Alert: {
        borderRadiusLG: theme.Border.Radius.Medium,
        colorInfo: theme.Color.Feedback.Info,
        colorInfoBg: theme.Color.Feedback.InfoLight,
        colorInfoBorder: theme.Color.Feedback.InfoSpecial,
        colorError: theme.Color.Feedback.Alert,
        colorErrorBg: theme.Color.Feedback.AlertLight,
        colorErrorBorder: theme.Color.Feedback.AlertSpecial,
        colorSuccessBorder: theme.Color.Feedback.Success,
        colorSuccessBg: theme.Color.Neutrals.Paper,
      },
      Badge: {
        colorBorderBg: theme.Color.Brand.Primary,
        colorText: theme.Color.TextContrast.Primary,
      },
      List: {},
      Typography: {
        linkDecoration: 'none',
        colorText: theme.Color.Neutrals.Text,
        colorLink: theme.Color.Neutrals.Text,
        colorLinkHover: theme.Color.Brand.Primary,
        fontSizeHeading4: 14,
        fontSizeHeading5: 14,
        titleMarginTop: theme.Spacing.Size.Medium,
        fontWeightStrong: 500,
      },
      Tooltip: {
        colorBgSpotlight: theme.Color.NeutralsFixed.Dark,
      },
      Tree: {
        nodeSelectedBg: theme.Color.Brand.Lightest,
        nodeHoverBg: theme.Color.Brand.Lightest,
      },
      Tour: {
        boxShadowTertiary: '0 0 0 1px rgba(255,255,255, 0.2)',
        motionDurationMid: '0',
        motionDurationSlow: '0',
        primaryNextBtnHoverBg: theme.Color.NeutralsFixed.Light,
      },

      Segmented: {
        itemColor: theme.Color.Neutrals.Text,
        itemActiveBg: theme.Color.Neutrals.Paper,
        trackBg: theme.Color.Neutrals.Light,
      },

      Progress: {
        remainingColor: theme.Color.Neutrals.Border,
        defaultColor: theme.Color.Brand.Primary,
      },
      Popover: {
        zIndexPopupBase: theme.ZIndex.Modal,
        colorBgElevated: theme.Color.Neutrals.Surface,
      },
      Collapse: {
        headerPadding: `${rem(theme.Spacing.Size.XSmall)} ${rem(theme.Spacing.Size.Small)}`,
        contentPadding: 0,
        colorTextHeading: theme.Color.Neutrals.Dark,
        borderRadiusLG: theme.Border.Radius.Small,
      },

      DatePicker: {
        colorPrimary: theme.Color.System.Primary,
        cellActiveWithRangeBg: theme.Components.Panel.BackgroundHover,
      },
      Drawer: {
        zIndexPopup: 1002,
      },
    },
  };

  return antdThemeConfig;
};
